import { KanbanColumn, KanbanSwimlane } from '../model/kanban.model';

const DEFAULT_COLUMN_WIDTH = '280px';
const COLLAPSED_COLUMN_WIDTH = 60;
const COLUMN_MARGIN_WIDTH = 10;
const SWIMLANE_COLUMN_WIDTH = 260;

export type KanbanSelectionResult = {
  lanes: KanbanSwimlane[];
  selectedItem: any;
};

export function cloneSwimlanes(lanes: KanbanSwimlane[] | null | undefined): KanbanSwimlane[] {
  return (lanes ?? []).map(lane => ({
    ...lane,
    columns: (lane.columns ?? []).map(column => ({
      ...column,
      items: [...(column.items ?? [])]
    }))
  }));
}

/**
 * Re-apply the collapse answers the operator gave, onto a freshly supplied set of lanes.
 *
 * Collapse is UI state this component owns, not something the caller's data carries, so a new input
 * array must not read as "everything is expanded again". Matching is by key: a lane or column that
 * is genuinely new has no prior answer and stays as the caller declared it.
 *
 * Returns the SAME objects wherever nothing has to change — the caller may have deliberately handed
 * back its previous lane objects to keep the DOM in place, and cloning them here would undo that.
 */
export function carryCollapseState(lanes: KanbanSwimlane[], previous: KanbanSwimlane[] | null | undefined): KanbanSwimlane[] {

  if (!previous?.length || !lanes?.length) return lanes;

  const priorLanes = new Map(previous.map(lane => [laneIdentity(lane), lane]));
  let changed = false;

  const next = lanes.map(lane => {
    const prior = priorLanes.get(laneIdentity(lane));
    if (!prior || prior === lane) return lane;

    const columns = carryColumnCollapse(lane.columns, prior.columns);
    const collapsed = lane.collapsed ?? prior.collapsed;
    if (columns === lane.columns && collapsed === lane.collapsed) return lane;

    changed = true;
    return { ...lane, collapsed, columns };
  });

  return changed ? next : lanes;
}

function laneIdentity(lane: KanbanSwimlane): string {
  return lane.key ?? lane.name;
}

function carryColumnCollapse(columns: KanbanColumn[] | undefined, priorColumns: KanbanColumn[] | undefined): KanbanColumn[] | undefined {

  if (!columns?.length || !priorColumns?.length) return columns;

  const priorByKey = new Map(priorColumns.map(column => [column.key, column]));
  let changed = false;

  const next = columns.map(column => {
    const prior = priorByKey.get(column.key);
    if (!prior || prior === column || column.collapsed !== undefined || prior.collapsed === undefined) return column;

    changed = true;
    return { ...column, collapsed: prior.collapsed };
  });

  return changed ? next : columns;
}

export function buildColumns(lanes: KanbanSwimlane[]): KanbanColumn[] {
  const columns = new Map<string, KanbanColumn>();

  for (const lane of lanes) {
    for (const column of lane.columns ?? []) {
      const currentColumn = columns.get(column.key);

      if (currentColumn) {
        currentColumn.hasItems = currentColumn.hasItems || (column.items?.length ?? 0) > 0;
        currentColumn.collapsed = currentColumn.collapsed ?? column.collapsed;
        continue;
      }

      columns.set(column.key, {
        ...column,
        items: [],
        hasItems: (column.items?.length ?? 0) > 0
      });
    }
  }

  return [...columns.values()];
}

export function syncSelection(lanes: KanbanSwimlane[], selectedItem: any, valueField: string, itemKey?: ((item: any) => string) | null): KanbanSelectionResult {
  if (!selectedItem || (!valueField && !itemKey)) {
    return { lanes, selectedItem: selectedItem ?? null };
  }

  // One identity for the whole sync, so a lane and its cards are matched the same way the board
  // compares selection — a parent-shaped row's id is not at `valueField`.
  const keyOf = (item: any) => (itemKey ? itemKey(item) : item?.[valueField]);
  const id = keyOf(selectedItem);

  if (!id) {
    return { lanes, selectedItem };
  }

  // Only a COLLAPSED lane holding the selection has anything to do here. Selecting a card in an
  // already-expanded lane must return the very same array — a new lane array re-renders every
  // swimlane and every card on the board, which is the wrong price for a highlight.
  let hasChange = false;
  const nextLanes = lanes.map(lane => {
    if (!lane.collapsed) {
      return lane;
    }

    const isLaneMatch = lane.data ? keyOf(lane.data) === id : false;
    const isColumnItemMatch = (lane.columns ?? []).some(column => (column.items ?? []).some(item => keyOf(item) === id));

    if (!isLaneMatch && !isColumnItemMatch) {
      return lane;
    }

    hasChange = true;
    return { ...lane, collapsed: false };
  });

  return {
    lanes: hasChange ? nextLanes : lanes,
    selectedItem
  };
}

export function calculateColumnWidth(columns: KanbanColumn[], showSwimlaneColumn: boolean): string {
  const expandedColumns = columns.filter(column => !column.collapsed).length;

  if (expandedColumns === 0) {
    return DEFAULT_COLUMN_WIDTH;
  }

  const collapsedColumns = columns.length - expandedColumns;
  const spaceForCollapsed = collapsedColumns > 0 ? collapsedColumns * COLLAPSED_COLUMN_WIDTH : 0;
  const spaceForSwimlane = showSwimlaneColumn ? SWIMLANE_COLUMN_WIDTH : 0;

  return `calc((100% + -${spaceForCollapsed + spaceForSwimlane}px)/${expandedColumns} + -${COLUMN_MARGIN_WIDTH}px)`;
}

export function buildGridColumns(columns: KanbanColumn[], showSwimlaneColumn: boolean): string {
  const swimlane = showSwimlaneColumn ? `${SWIMLANE_COLUMN_WIDTH}px ` : '';
  const cols = columns.map(c => c.collapsed ? `${COLLAPSED_COLUMN_WIDTH}px` : 'var(--local-kanban-column-width)').join(' ');
  return swimlane + cols;
}
